/**
 * Authentication Hook
 * Requirements: 1.4, 1.7, 3.2
 */
import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import {
  signIn,
  signOut,
  getCurrentUser,
  fetchAuthSession,
  AuthUser
} from 'aws-amplify/auth';
import { isLocalMode } from '../config/amplify';

interface AuthContextType {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  getToken: () => Promise<string | null>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  async function checkAuth() {
    try {
      const currentUser = await getCurrentUser();
      setUser(currentUser);
    } catch {
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }

  async function login(username: string, password: string) {
    const result = await signIn({ username, password });
    if (result.isSignedIn) {
      const currentUser = await getCurrentUser();
      setUser(currentUser);
    }
  }

  async function logout() {
    await signOut();
    setUser(null);
  }

  async function getToken(): Promise<string | null> {
    try {
      const session = await fetchAuthSession();
      return session.tokens?.idToken?.toString() || null;
    } catch {
      return null;
    }
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        login,
        logout,
        getToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

/**
 * Local development auth provider — no Cognito, reads user from localStorage.
 */
export function LocalAuthProvider({ children }: { children: ReactNode }) {
  const [isLoading, setIsLoading] = useState(true);

  // Create a mock AuthUser-like object from localStorage
  const userId = localStorage.getItem('local_user_id') || '';
  const mockUser = userId ? { userId, username: localStorage.getItem('local_user_name') || userId } as unknown as AuthUser : null;

  useEffect(() => {
    // Short delay to match real auth flow timing
    setIsLoading(false);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user: mockUser,
        isAuthenticated: !!userId,
        isLoading,
        login: async () => {},
        logout: async () => {
          localStorage.removeItem('local_user_id');
          localStorage.removeItem('local_user_email');
          localStorage.removeItem('local_user_name');
          window.location.reload();
        },
        getToken: async () => 'local-token',
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export { isLocalMode };
